<script>
import TerrainRaycastWorld from "@/components/terrain-raycast-preview/TerrainRaycastWorld.vue";
import "@/assets/fonts/font-awesome-4.7.0/css/font-awesome.min.css";

export default {
  name: "TerrainRaycastPreview",
  components: {
    TerrainRaycastWorld
  },
  data() {
    return {
      locations: [
        {
          id: "deposit",
          label: "แจ้งฝาก",
          shortHint: "ธนาคารเหรียญเข้า",
          icon: "fa-download",
          glyph: "↓",
          accent: "#45e0ae",
          worldX: -18,
          worldZ: -14
        },
        {
          id: "withdraw",
          label: "แจ้งถอน",
          shortHint: "ตู้เซฟเหรียญออก",
          icon: "fa-upload",
          glyph: "↑",
          accent: "#64c6ff",
          worldX: 18,
          worldZ: -14
        },
        {
          id: "spin",
          label: "Spin",
          shortHint: "วงล้อตั้งหมุนจริง",
          icon: "fa-circle-o-notch",
          glyph: "S",
          accent: "#ff5f91",
          worldX: -21,
          worldZ: 1
        },
        {
          id: "roulette",
          label: "Roulette",
          shortHint: "โต๊ะวงล้อลูกบอล",
          icon: "fa-dot-circle-o",
          glyph: "R",
          accent: "#ff765d",
          worldX: 21,
          worldZ: 1
        },
        {
          id: "reward",
          label: "แลกของรางวัล",
          shortHint: "ร้านกล่องของขวัญ",
          icon: "fa-gift",
          glyph: "★",
          accent: "#f5c95c",
          worldX: -14,
          worldZ: 17
        },
        {
          id: "friend",
          label: "แนะนำเพื่อน",
          shortHint: "กิลด์เพื่อนสองคน",
          icon: "fa-users",
          glyph: "+",
          accent: "#b98cf2",
          worldX: 14,
          worldZ: 17
        },
        {
          id: "play",
          label: "เข้าเล่น",
          shortHint: "ประตูเกมกลางเกาะ",
          icon: "fa-play",
          glyph: "▶",
          accent: "#ff9b54",
          worldX: 0,
          worldZ: 0
        }
      ],
      activeId: "play",
      lastRaycast: {
        type: "landmark",
        label: "เข้าเล่น",
        point: null
      },
      engineError: false
    };
  },
  computed: {
    activeLocation() {
      return (
        this.locations.find(location => location.id === this.activeId) ||
        this.locations[0]
      );
    },
    raycastHeadline() {
      return this.lastRaycast.type === "terrain"
        ? "Terrain hit"
        : this.lastRaycast.label;
    },
    coordinateLabel() {
      const point = this.lastRaycast.point;
      if (!point) return "แตะพื้นเพื่ออ่านพิกัด X / Y / Z แบบ real-time";
      return `X ${point.x.toFixed(1)} · Y ${point.y.toFixed(
        1
      )} · Z ${point.z.toFixed(1)}`;
    },
    activeAccentStyle() {
      return {
        "--active-accent": this.activeLocation.accent
      };
    }
  },
  methods: {
    handleSelect(payload) {
      const location = this.locations.find(item => item.id === payload.id);
      if (!location) return;
      this.activeId = payload.id;
      this.lastRaycast = {
        type: "landmark",
        label: location.label,
        point: payload.point
      };
      if (navigator.vibrate) navigator.vibrate(12);
    },
    handleTerrainPick(point) {
      this.lastRaycast = {
        type: "terrain",
        label: "Terrain hit",
        point
      };
      if (navigator.vibrate) navigator.vibrate(8);
    },
    selectShortcut(id) {
      const location = this.locations.find(item => item.id === id);
      if (!location) return;
      this.activeId = id;
      this.lastRaycast = {
        type: "landmark",
        label: location.label,
        point: null
      };
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    }
  }
};
</script>

<template>
  <main class="terrain-preview" :style="activeAccentStyle">
    <TerrainRaycastWorld
      :locations="locations"
      :active-id="activeId"
      @select="handleSelect"
      @terrain-pick="handleTerrainPick"
      @engine-error="engineError = true"
    />

    <header class="terrain-header">
      <div class="terrain-header__brand">
        <div class="terrain-header__logo-shell">
          <img
            class="terrain-header__logo"
            src="../assets/images/logo2.png"
            alt="UFA Easy888"
          />
        </div>
        <div class="terrain-header__copy">
          <small>THREE.JS EXPERIMENT</small>
          <strong>TERRAIN KINGDOM</strong>
        </div>
      </div>

      <button
        class="terrain-header__fullscreen"
        type="button"
        aria-label="เปิดเต็มหน้าจอ"
        @click="toggleFullscreen"
      >
        <i class="fa fa-expand" aria-hidden="true"></i>
      </button>
    </header>

    <div class="terrain-tech" aria-label="สถานะระบบสามมิติ">
      <span><i></i> WEBGL LIVE</span>
      <span>REAL TERRAIN</span>
      <span>RAYCAST ON</span>
    </div>

    <div class="terrain-instruction">
      <i class="fa fa-hand-pointer-o" aria-hidden="true"></i>
      <span>ลากเพื่อหมุน · จีบเพื่อซูม · แตะพื้นหรืออาคาร</span>
    </div>

    <section class="terrain-panel" aria-live="polite">
      <div class="terrain-panel__handle"></div>

      <nav class="terrain-panel__shortcuts" aria-label="ทางลัดอาคาร">
        <button
          v-for="location in locations"
          :key="location.id"
          type="button"
          :class="{ 'is-active': activeId === location.id }"
          :aria-label="location.label"
          :aria-pressed="activeId === location.id ? 'true' : 'false'"
          :style="{ '--item-accent': location.accent }"
          @click="selectShortcut(location.id)"
        >
          <i class="fa" :class="location.icon" aria-hidden="true"></i>
        </button>
      </nav>

      <div class="terrain-panel__readout">
        <div class="terrain-panel__icon">
          <i
            v-if="lastRaycast.type === 'landmark'"
            class="fa"
            :class="activeLocation.icon"
            aria-hidden="true"
          ></i>
          <i v-else class="fa fa-crosshairs" aria-hidden="true"></i>
        </div>

        <div class="terrain-panel__copy">
          <small>
            {{
              lastRaycast.type === "terrain"
                ? "RAYCAST · SURFACE"
                : "RAYCAST · LANDMARK"
            }}
          </small>
          <strong>{{ raycastHeadline }}</strong>
          <p>{{ coordinateLabel }}</p>
        </div>

        <span class="terrain-panel__status">
          <i></i>
          HIT
        </span>
      </div>

      <div class="terrain-panel__note">
        <i class="fa fa-bolt" aria-hidden="true"></i>
        <span v-if="!engineError">
          อาคารและพื้นเป็น geometry จริง ไม่ใช่ภาพแบน
        </span>
        <span v-else>อุปกรณ์นี้เปิด WebGL ไม่สำเร็จ</span>
      </div>
    </section>
  </main>
</template>

<style scoped>
.terrain-preview {
  position: fixed;
  inset: 0;
  overflow: hidden;
  color: #ffffff;
  background: #071c35;
  font-family: "Sukhumvit Set", "Thonburi", "Tahoma", sans-serif;
  -webkit-font-smoothing: antialiased;
}

.terrain-header {
  position: absolute;
  top: max(14px, env(safe-area-inset-top));
  right: 15px;
  left: 15px;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
}

.terrain-header__brand {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 8px 15px 8px 9px;
  border: 1px solid rgba(154, 235, 242, 0.24);
  border-radius: 22px;
  background: linear-gradient(
    135deg,
    rgba(7, 29, 51, 0.88),
    rgba(10, 36, 61, 0.68)
  );
  box-shadow: 0 16px 36px rgba(1, 9, 25, 0.36);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.terrain-header__logo-shell {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 43px;
  height: 43px;
  margin-right: 10px;
  border: 1px solid rgba(255, 206, 100, 0.5);
  border-radius: 15px;
  background: radial-gradient(circle, #8d6a1a, #3a2b14);
}

.terrain-header__logo {
  width: 35px;
  height: 35px;
  object-fit: contain;
}

.terrain-header__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.terrain-header__copy small {
  margin-bottom: 2px;
  color: #71eddf;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.17em;
}

.terrain-header__copy strong {
  overflow: hidden;
  max-width: 180px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.terrain-header__fullscreen {
  appearance: none;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  padding: 0;
  color: #ffffff;
  border: 1px solid rgba(154, 235, 242, 0.25);
  border-radius: 18px;
  background: rgba(6, 27, 48, 0.74);
  box-shadow: 0 12px 32px rgba(1, 9, 25, 0.35);
  cursor: pointer;
  pointer-events: auto;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.terrain-header__fullscreen:active {
  color: #73f5e6;
  transform: scale(0.94);
}

.terrain-tech {
  position: absolute;
  top: max(84px, calc(env(safe-area-inset-top) + 73px));
  left: 50%;
  z-index: 7;
  display: flex;
  gap: 7px;
  align-items: center;
  width: max-content;
  max-width: calc(100vw - 30px);
  transform: translateX(-50%);
}

.terrain-tech span {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  padding: 6px 9px;
  color: rgba(225, 250, 255, 0.86);
  border: 1px solid rgba(133, 227, 239, 0.18);
  border-radius: 999px;
  background: rgba(6, 25, 46, 0.66);
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.12em;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.terrain-tech span:first-child i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #55efbf;
  box-shadow: 0 0 10px #55efbf;
}

.terrain-instruction {
  position: absolute;
  top: 121px;
  left: 50%;
  z-index: 7;
  display: flex;
  gap: 8px;
  align-items: center;
  width: max-content;
  max-width: calc(100vw - 28px);
  padding: 8px 13px;
  color: #d8f8fb;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(5, 24, 43, 0.66);
  font-size: 10px;
  font-weight: 650;
  transform: translateX(-50%);
  pointer-events: none;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.terrain-instruction i {
  color: #ffcb72;
}

.terrain-panel {
  position: absolute;
  right: 13px;
  bottom: max(12px, env(safe-area-inset-bottom));
  left: 13px;
  z-index: 9;
  padding: 11px 12px 13px;
  border: 1px solid rgba(143, 228, 237, 0.24);
  border-radius: 25px;
  background: linear-gradient(
    145deg,
    rgba(6, 24, 43, 0.94),
    rgba(9, 36, 58, 0.91)
  );
  box-shadow: 0 24px 50px rgba(1, 9, 24, 0.48),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.terrain-panel__handle {
  width: 42px;
  height: 4px;
  margin: -2px auto 8px;
  border-radius: 999px;
  background: rgba(185, 237, 241, 0.32);
}

.terrain-panel__shortcuts {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
  margin-bottom: 10px;
}

.terrain-panel__shortcuts button {
  appearance: none;
  display: grid;
  place-items: center;
  min-width: 0;
  height: 37px;
  padding: 0;
  color: rgba(224, 248, 251, 0.72);
  border: 1px solid rgba(143, 228, 237, 0.13);
  border-radius: 12px;
  background: rgba(3, 17, 33, 0.58);
  cursor: pointer;
}

.terrain-panel__shortcuts button.is-active {
  color: #ffffff;
  border-color: var(--item-accent);
  background: color-mix(in srgb, var(--item-accent) 22%, rgba(3, 17, 33, 0.72));
  box-shadow: 0 0 16px color-mix(in srgb, var(--item-accent) 36%, transparent);
}

.terrain-panel__shortcuts button:active {
  transform: scale(0.92);
}

.terrain-panel__readout {
  position: relative;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 11px;
  align-items: center;
  padding: 11px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--active-accent) 52%, transparent);
  border-radius: 18px;
  background: linear-gradient(
    105deg,
    color-mix(in srgb, var(--active-accent) 13%, rgba(5, 23, 41, 0.88)),
    rgba(4, 20, 37, 0.78)
  );
}

.terrain-panel__icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  color: #ffffff;
  border: 1px solid color-mix(in srgb, var(--active-accent) 64%, white);
  border-radius: 16px;
  background: color-mix(
    in srgb,
    var(--active-accent) 34%,
    rgba(4, 20, 37, 0.9)
  );
  box-shadow: 0 0 18px color-mix(in srgb, var(--active-accent) 32%, transparent);
}

.terrain-panel__copy {
  min-width: 0;
}

.terrain-panel__copy small {
  display: block;
  margin-bottom: 1px;
  color: #79eadf;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.terrain-panel__copy strong {
  display: block;
  overflow: hidden;
  color: #ffffff;
  font-size: 17px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.terrain-panel__copy p {
  overflow: hidden;
  margin: 2px 0 0;
  color: rgba(211, 238, 241, 0.68);
  font-size: 10px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.terrain-panel__status {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  padding: 6px 8px;
  color: #73f5d7;
  border: 1px solid rgba(115, 245, 215, 0.24);
  border-radius: 999px;
  background: rgba(29, 112, 95, 0.14);
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.12em;
}

.terrain-panel__status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #73f5d7;
  box-shadow: 0 0 9px #73f5d7;
}

.terrain-panel__note {
  display: flex;
  gap: 7px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  color: rgba(199, 231, 235, 0.58);
  font-size: 9px;
}

.terrain-panel__note i {
  color: #ffbf62;
}

@supports not (color: color-mix(in srgb, red, blue)) {
  .terrain-panel__shortcuts button.is-active {
    background: rgba(37, 81, 95, 0.92);
    box-shadow: 0 0 15px rgba(93, 228, 226, 0.28);
  }

  .terrain-panel__readout {
    border-color: rgba(96, 221, 218, 0.42);
    background: rgba(7, 35, 55, 0.9);
  }

  .terrain-panel__icon {
    border-color: rgba(255, 194, 102, 0.62);
    background: rgba(41, 84, 94, 0.92);
  }
}

@media (max-width: 360px) {
  .terrain-header__copy strong {
    max-width: 140px;
    font-size: 12px;
  }

  .terrain-tech span {
    padding-right: 7px;
    padding-left: 7px;
    font-size: 6px;
  }

  .terrain-instruction {
    font-size: 9px;
  }

  .terrain-panel {
    right: 9px;
    left: 9px;
  }

  .terrain-panel__shortcuts {
    gap: 4px;
  }

  .terrain-panel__readout {
    grid-template-columns: 44px minmax(0, 1fr);
  }

  .terrain-panel__icon {
    width: 44px;
    height: 44px;
  }

  .terrain-panel__status {
    display: none;
  }
}
</style>
